import * as m from '@staiter/i18n';
import { format, isValid, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar, X } from 'lucide-react';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DayPicker } from 'react-day-picker';
import { createPortal } from 'react-dom';
import 'react-day-picker/style.css';
import styles from './DatePicker.module.css';

/**
 * F2 — DatePicker (custom, react-day-picker under the hood).
 *
 * Заменяет нативный `<input type="date">`, который UX-непредсказуем:
 * Chrome / Safari сбрасывают значение при blur, mobile — разный вид,
 * нет quick-presets.
 *
 * API контракт:
 *   - `value`: ISO date (YYYY-MM-DD) или null
 *   - `onChange(next)`: ISO date или null (очистка)
 *   - Локаль: ru (format dd.MM.yyyy), Monday first day
 */

const DATE_FORMAT = 'dd.MM.yyyy';

export type DatePickerProps = {
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  error?: string | undefined;
  placeholder?: string;
  className?: string;
  id?: string;
};

function toDateOrNull(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isValid(d) ? d : null;
}

function toIsoDate(d: Date | undefined): string | null {
  if (!d || !isValid(d)) return null;
  return format(d, 'yyyy-MM-dd');
}

export function DatePicker({
  label,
  value,
  onChange,
  min,
  max,
  required,
  disabled,
  hint,
  error,
  placeholder,
  className,
  id,
}: DatePickerProps): JSX.Element {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const hintId = hint ? `${triggerId}-hint` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  // S-FIX-5-FE.1.2: portal target — ближайший `<dialog>` (если DatePicker внутри
  // модалки). Native `<dialog>` живёт в Top Layer браузера — portal в `document.body`
  // окажется ПОД ним. Чтобы panel был поверх dialog-backdrop, рендерим его как child
  // того же dialog (попадает в тот же Top Layer).
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const selected = useMemo(() => toDateOrNull(value), [value]);
  const formatted = selected ? format(selected, DATE_FORMAT) : '';

  // S-FIX-2-FE.2.1: smart positioning (mirror логики DateRangePicker).
  const positionPanel = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const rect = t.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const panelH = panelRef.current?.offsetHeight ?? 320;
    const panelW = panelRef.current?.offsetWidth ?? 280;

    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;

    let top: number;
    if (spaceBelow >= panelH + 8) {
      top = rect.bottom + 4;
    } else if (spaceAbove >= panelH + 8) {
      top = rect.top - panelH - 4;
    } else {
      top = 8;
    }

    let left = rect.left;
    if (left + panelW > viewportW - 8) {
      left = Math.max(8, viewportW - panelW - 8);
    }

    setPanelPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    positionPanel();
    // S-FIX-5-FE.1.2: portal-to-dialog detection.
    const closestDialog = triggerRef.current?.closest('dialog') ?? null;
    setPortalTarget(
      closestDialog instanceof HTMLElement
        ? closestDialog
        : typeof document !== 'undefined'
          ? document.body
          : null,
    );
    const onScroll = () => positionPanel();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const applyToday = () => {
    onChange(format(new Date(), 'yyyy-MM-dd'));
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
  };

  const minDate = toDateOrNull(min ?? null) ?? undefined;
  const maxDate = toDateOrNull(max ?? null) ?? undefined;

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={triggerId} className={styles.label}>
        {label}
        {required && !(typeof label === 'string' && label.trim().endsWith('*')) && (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
        {/* F100 (С-I, круг 6, UX): звёздочка скрыта от чтеца, а `aria-required` на кнопке-триггере
            недопустим (роль button) — обязательность произносится текстом подписи. */}
        {required && <span className={styles.srOnly}>{m.primitive_required_sr()}</span>}
      </label>
      <button
        id={triggerId}
        ref={triggerRef}
        type="button"
        className={[
          styles.trigger,
          error ? styles.triggerError : '',
          disabled ? styles.triggerDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
      >
        <span className={styles.icon} aria-hidden="true">
          <Calendar size={14} />
        </span>
        <span className={[styles.value, formatted ? '' : styles.placeholder].join(' ')}>
          {formatted || (placeholder ?? m.primitive_date_placeholder())}
        </span>
        {formatted && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Очистить"
            className={styles.clear}
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                clear();
              }
            }}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open &&
        panelPos &&
        portalTarget &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={{ top: panelPos.top, left: panelPos.left }}
            role="dialog"
            aria-label={label}
            /**
             * F5 — Esc внутри календаря закрывает календарь, а не панель под ним.
             * Раньше нажатие обрабатывалось только на ТРИГГЕРЕ: стоило перевести
             * фокус в сетку дат, и Esc уходил в peek — карточка закрывалась, а
             * календарь оставался висеть (нарушение I4).
             */
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              triggerRef.current?.focus();
            }}
          >
            <div className={styles.quickRow}>
              <button type="button" className={styles.quickBtn} onClick={applyToday}>
                {m.component_date_picker_today()}
              </button>
              <button
                type="button"
                className={styles.quickBtn}
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() - 1);
                  onChange(format(d, 'yyyy-MM-dd'));
                  setOpen(false);
                }}
              >
                {m.component_date_picker_yesterday()}
              </button>
            </div>
            <DayPicker
              mode="single"
              selected={selected ?? undefined}
              onSelect={(d) => {
                const iso = toIsoDate(d);
                onChange(iso);
                setOpen(false);
              }}
              locale={ru}
              weekStartsOn={1}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
              autoFocus
            />
            <div className={styles.footer}>
              <button type="button" className={styles.footerBtn} onClick={clear}>
                {m.component_date_picker_clear()}
              </button>
              <button type="button" className={styles.footerBtn} onClick={() => setOpen(false)}>
                {m.common_button_close()}
              </button>
            </div>
          </div>,
          portalTarget,
        )}

      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * F2 — typing helper (для future manual typeahead input; сейчас не используется в trigger).
 */
export function parseDmy(input: string): string | null {
  const d = parse(input, DATE_FORMAT, new Date());
  if (!isValid(d)) return null;
  return format(d, 'yyyy-MM-dd');
}
